import {parseRecordCommand,type RecordCommandReceipt} from '@secretaa/contracts-data';
import {ApiError,recordCommandDigest} from '@secretaa/sdk';
import {Button} from '@secretaa/ui';
import {useEffect,useRef,useState,type ReactNode} from 'react';
import {useBlocker} from 'react-router';
import {useTranslation} from 'react-i18next';
import {api} from '../../app/api.ts';
import {useSession} from '../../app/session.ts';
import {errorMessage} from '../../shared/errors.ts';
import {FormError} from '../../shared/form.tsx';

import {clearRecordCommandJournal,saveRecordCommandJournal,type JournalSlot} from './record-command-journal.ts';

type Command = ReturnType<typeof parseRecordCommand>;
type Props = {
 workspaceId:string;projectId:string;collectionId:string;manifestHash:string;
 operation:{kind:'CREATE'}|{kind:'UPDATE';recordId:string;expectedVersion:number};
 input:Command['input'];children:ReactNode;onSuccess:()=>Promise<unknown>;
 onLocked:(locked:boolean)=>void;onCancel?:()=>void;beforeDispatch?:(signal:AbortSignal)=>Promise<void>;
};

/** Keeps uncertain writes immutable in memory. Never puts customer field values in browser storage. */
export function RecordCommandForm(props:Props){
 const {t}=useTranslation(),session=useSession();
 const [busy,setBusy]=useState(false),[uncertain,setUncertain]=useState(false),[receipt,setReceipt]=useState<RecordCommandReceipt|null>(null),[error,setError]=useState<unknown>(null);
 const pending=useRef<AbortController|null>(null),command=useRef<Command|null>(null),hadUncertain=useRef(false);
 const initial=useRef({workspaceId:props.workspaceId,projectId:props.projectId,collectionId:props.collectionId,manifestHash:props.manifestHash,operation:props.operation,subjectId:session.data?.user.id});
 const frame=initial.current;
 const slot:JournalSlot={workspaceId:frame.workspaceId,projectId:frame.projectId,collectionId:frame.collectionId,subjectId:frame.subjectId??'',recordId:frame.operation.kind==='UPDATE'?frame.operation.recordId:null};
 const authorized=!!frame.subjectId&&session.data?.user.id===frame.subjectId&&!session.isError;
 const locked=busy||uncertain||!!receipt;
 const blocker=useBlocker(locked);
 useEffect(()=>{props.onLocked(locked);},[locked,props.onLocked]);
 useEffect(()=>()=>pending.current?.abort(),[]);
 useEffect(()=>{if(!authorized)pending.current?.abort();},[authorized]);
 useEffect(()=>{
  if(!locked)return;
  const warn=(event:BeforeUnloadEvent)=>{event.preventDefault();event.returnValue='';};
  window.addEventListener('beforeunload',warn);return()=>window.removeEventListener('beforeunload',warn);
 },[locked]);
 const refresh=async()=>{
  setBusy(true);setError(null);
  try{if(receipt&&command.current)clearRecordCommandJournal(slot,command.current.idempotencyKey);await props.onSuccess();}catch(failure){setError(failure);}finally{setBusy(false);}
 };
 const submit=async()=>{
  if(pending.current||busy||receipt||!authorized)return;
  const controller=new AbortController();pending.current=controller;setBusy(true);setError(null);
  let dispatched=false;
  try{
   if(!command.current&&props.beforeDispatch)await props.beforeDispatch(controller.signal);
   if(controller.signal.aborted)return;
   command.current??=parseRecordCommand({operation:frame.operation.kind,idempotencyKey:crypto.randomUUID(),manifestHash:frame.manifestHash,input:props.input,...(frame.operation.kind==='UPDATE'?{recordId:frame.operation.recordId,expectedVersion:frame.operation.expectedVersion}:{})});
   const digest=await recordCommandDigest(frame.projectId,frame.collectionId,command.current);
   if(controller.signal.aborted)return;
   saveRecordCommandJournal({version:1,slot,idempotencyKey:command.current.idempotencyKey,operation:command.current.operation,commandDigest:digest,expectedVersion:command.current.operation==='UPDATE'?command.current.expectedVersion:null});
   dispatched=true;
   const result=await api.recordCommands.execute(frame.workspaceId,frame.projectId,frame.collectionId,command.current,controller.signal);
   if(controller.signal.aborted)return;
   if(result.subjectId!==frame.subjectId)throw new ApiError(502,'CONTRACT_MISMATCH','Record command subject mismatch');
   setReceipt(result);setUncertain(false);
  }catch(failure){
   if(controller.signal.aborted)return;
   setError(failure);
   const rejected=!dispatched||(!hadUncertain.current&&failure instanceof ApiError&&[400,401,403,404,409,422].includes(failure.status));
   if(rejected){try{if(dispatched&&command.current)clearRecordCommandJournal(slot,command.current.idempotencyKey);command.current=null;setUncertain(false);}catch(storageFailure){setError(storageFailure);hadUncertain.current=true;setUncertain(true);}}else{hadUncertain.current=true;setUncertain(true);}
  }finally{if(pending.current===controller){pending.current=null;setBusy(false);}}
 };
 // No success side effect inside the mutation catch: a failed refresh must never resend a committed write.
 useEffect(()=>{if(receipt&&authorized)void refresh();},[receipt]);
 if(!authorized)return <FormError message={t('recordCommand.sessionChanged')}/>;
 return <form aria-label={t(frame.operation.kind==='CREATE'?'data.record.new':'common.edit')} className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto" onSubmit={event=>{event.preventDefault();void submit();}}>
  <FormError message={error?errorMessage(t,error):null}/>
  {uncertain?<p role="status">{t('recordCommand.uncertain')}</p>:null}
  {receipt?<p role="status">{t('recordCommand.saved')}</p>:null}
  <fieldset disabled={locked} className="contents">{props.children}</fieldset>
  <div className="flex flex-wrap gap-2">
   {receipt?<Button type="button" disabled={busy} onClick={()=>void refresh()}>{t('recordCommand.refresh')}</Button>:<Button type="submit" disabled={busy}>{t(busy?'recordCommand.working':uncertain?'recordCommand.recover':frame.operation.kind==='CREATE'?'common.create':'common.save')}</Button>}
   {props.onCancel?<Button type="button" variant="secondary" disabled={locked} onClick={props.onCancel}>{t('common.cancel')}</Button>:null}
  </div>
  {blocker.state==='blocked'?<div role="alert" className="space-y-2"><p>{t('recordCommand.leaveWarning')}</p><Button type="button" onClick={()=>blocker.reset()}>{t('recordCommand.stay')}</Button></div>:null}
 </form>;
}
